import React, { useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, FormControl, InputLabel, Select, MenuItem, Box, TextField, OutlinedInput, Checkbox, ListItemText } from '@mui/material';
import Slide from '@mui/material/Slide';
import { useSelector } from 'react-redux';
import { useGetSitesQuery, useGetDevicesQuery, useGetAllUsersQuery } from '../../services/api';
import { useTheme } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const AddGroupDialogBox = ({
    open,
    handleCloseDialog,
    handleAddGroup,
    isAddLoading,
    selectedSites,
    setSelectedSites,
    selectedDevices,
    setSelectedDevices,
    selectedUsers,
    setSelectedUsers,
    groupName,
    setGroupName,
}) => {
    const theme = useTheme();
    const { company } = useSelector((state) => state.global);

    // API
    const { data: sitesData } = useGetSitesQuery(company);
    const { data: devicesData } = useGetDevicesQuery(company);
    const { data: usersData } = useGetAllUsersQuery(company);

    // Filter recived data
    const filteredSites = useMemo(() => {
        return sitesData?.filter((site) => site.company._id === company && site.enabled) || [];
    }, [sitesData, company]);

    const filteredDevices = useMemo(() => {
        return devicesData?.filter((device) => device.company._id === company && device.addedToDashboard) || [];
    }, [devicesData, company]);

    const filteredUsers = useMemo(() => {
        return usersData?.users?.filter((user) => user.company?._id === company) || [];
    }, [usersData, company]);

    // Handle site change
    const handleSiteChange = (event) => {
        setSelectedSites(event.target.value);
    };

    // Handle device change
    const handleDeviceChange = (event) => {
        setSelectedDevices(event.target.value);
    };

    // Handle user change
    const handleUserChange = (event) => {
        setSelectedUsers(event.target.value);
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={handleCloseDialog}
            aria-describedby="add-device-dialog-description"
            PaperProps={{
                sx: { backgroundColor: theme.palette.background.paper },
            }}
        >
            <DialogTitle variant="h2" fontWeight="bold">
                Add Group
            </DialogTitle>
            <DialogContent sx={{ width: '500px', padding: '20px' }}>
                <Box display="flex" flexDirection="column" gap={2} sx={{ mt: '1rem' }}>
                    <TextField label="Group Name" variant="outlined" value={groupName} onChange={(e) => setGroupName(e.target.value)} fullWidth />

                    {/* Multi-select Sites */}
                    <FormControl fullWidth>
                        <InputLabel id="site-select-label">Select Sites</InputLabel>
                        <Select
                            labelId="site-select-label"
                            id="site-select"
                            multiple
                            value={selectedSites}
                            onChange={handleSiteChange}
                            input={<OutlinedInput label="Select Sites" />}
                            renderValue={(selected) =>
                                selected
                                    .map((id) => {
                                        const site = filteredSites.find((s) => s._id === id);
                                        return site?.name || id;
                                    })
                                    .join(', ')
                            }
                        >
                            {filteredSites.map((site) => (
                                <MenuItem key={site._id} value={site._id}>
                                    <Checkbox checked={selectedSites.indexOf(site._id) > -1} />
                                    <ListItemText primary={site.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Multi-select Devices */}
                    <FormControl fullWidth>
                        <InputLabel id="device-select-label">Select Devices</InputLabel>
                        <Select
                            labelId="device-select-label"
                            multiple
                            value={selectedDevices}
                            onChange={handleDeviceChange}
                            input={<OutlinedInput label="Select Devices" />}
                            renderValue={(selected) =>
                                selected
                                    .map((id) => {
                                        const device = filteredDevices.find((d) => d._id === id);
                                        return device?.name || id;
                                    })
                                    .join(', ')
                            }
                        >
                            {filteredDevices.map((device) => (
                                <MenuItem key={device._id} value={device._id}>
                                    <Checkbox checked={selectedDevices.indexOf(device._id) > -1} />
                                    <ListItemText primary={device.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Multi-select Users */}
                    <FormControl fullWidth>
                        <InputLabel id="user-select-label">Select Users</InputLabel>
                        <Select
                            labelId="user-select-label"
                            multiple
                            value={selectedUsers}
                            onChange={handleUserChange}
                            input={<OutlinedInput label="Select Users" />}
                            renderValue={(selected) =>
                                selected
                                    .map((id) => {
                                        const user = filteredUsers.find((u) => u._id === id);
                                        return user?.name || id;
                                    })
                                    .join(', ')
                            }
                        >
                            {filteredUsers.map((user) => (
                                <MenuItem key={user._id} value={user._id}>
                                    <Checkbox checked={selectedUsers.indexOf(user._id) > -1} />
                                    <ListItemText primary={user.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box mt={1} ml={1} color="text.secondary" fontSize="0.875rem">
                        <strong>Note:</strong> Each user must be added to at least one group to view data.
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button
                    onClick={handleCloseDialog}
                    variant="contained"
                    sx={{
                        color: theme.palette.text.black,
                        backgroundColor: theme.palette.text.white,
                        '&:hover': {
                            backgroundColor: theme.palette.text.white,
                        },
                        '&:focus': { outline: 'none' },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleAddGroup}
                    variant="contained"
                    sx={{
                        color: theme.palette.text.black,
                        backgroundColor: theme.palette.text.white,
                        '&:hover': {
                            backgroundColor: theme.palette.text.white,
                        },
                        '&:focus': { outline: 'none' },
                    }}
                >
                    {isAddLoading ? 'Adding...' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddGroupDialogBox;
